import { jwtDecode } from 'jwt-decode';
import { DEVELOPER_ROLE, rolesFromToken } from 'src/Common/navigation/developerAccess';

// =====================================================================================
// WHO IS THIS BROWSER, AND MAY IT ACT AS A STORE DEVICE?
//
// One browser profile (per origin) = one device of the device-settings registry, appType 'web'.
// The id lives in localStorage `cloudpos_device_id`, survives logout and company changes, and is
// shared by every tab and by the developer "Launch" popup (same origin, same localStorage). The
// Stock Audit module minted its own per-browser id first (`cloudpos_stock_audit_device_id`, stamped
// on every offline scan row): a browser that has one ADOPTS it, so scans and the registry name the
// same machine. Stock Audit's getDeviceId delegates here.
//
// Only a real STORE session may register, apply, report or join the device group. Never:
//   - the developer "Launch" popup (sessionStorage.impersonateToken, or still on its ?devLaunch= URL),
//     because it shares localStorage with the developer's own window;
//   - a same-window developer "Login as" token (claim isDeveloperLogin = "true");
//   - a reseller token (claim isResellerLogin = "true");
//   - a developer's own session (Developer role) — the Developer hub is not a store device.
// =====================================================================================

const WEB_DEVICE_ID_KEY = 'cloudpos_device_id';
const STOCK_AUDIT_DEVICE_ID_KEY = 'cloudpos_stock_audit_device_id';
const STOCK_AUDIT_DEVICE_NAME_KEY = 'cloudpos_stock_audit_device_name';

/** The server's deviceId rule. */
const DEVICE_ID_PATTERN = /^[A-Za-z0-9_-]{8,64}$/;

/**
 * Window event fired by every login / logout path of THIS tab (the browser 'storage' event only
 * reaches the other tabs). NotificationProvider restarts its hub connection on it.
 */
export const AUTH_TOKEN_CHANGED_EVENT = 'authTokenChanged';

export function notifyAuthTokenChanged(): void {
  try {
    window.dispatchEvent(new Event(AUTH_TOKEN_CHANGED_EVENT));
  } catch {
    /* ignore */
  }
}

// Captured when the bundle loads: App.tsx strips ?devLaunch= from the URL in an effect that runs
// AFTER the providers' effects, so a later read of location.search would miss it.
const OPENED_AS_IMPERSONATION_POPUP = (() => {
  try {
    const params = new URLSearchParams(window.location.search);
    return params.has('devLaunch') || params.has('devToken');
  } catch {
    return false;
  }
})();

const randomHex = (bytes: number): string | null => {
  try {
    const buf = new Uint8Array(bytes);
    crypto.getRandomValues(buf);
    return Array.from(buf, (b) => b.toString(16).padStart(2, '0')).join('');
  } catch {
    return null;
  }
};

const mintDeviceId = (): string => {
  try {
    if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
      return `web-${crypto.randomUUID()}`;
    }
  } catch {
    /* randomUUID exists only in secure contexts (https / localhost) */
  }
  const hex = randomHex(16);
  if (hex) return `web-${hex}`;
  let out = '';
  while (out.length < 24) out += Math.random().toString(36).slice(2);
  return `web-${out.slice(0, 24)}`;
};

const validId = (v: string | null | undefined): v is string => typeof v === 'string' && DEVICE_ID_PATTERN.test(v);

/**
 * The PERSISTED device id of this browser, created (or adopted from Stock Audit) on first use.
 * Returns null when browser storage is unavailable: an id that is not stored would change on
 * every load, and an unstable id must never be registered.
 */
export function getPersistedWebDeviceId(): string | null {
  try {
    const existing = localStorage.getItem(WEB_DEVICE_ID_KEY);
    if (validId(existing)) return existing;
    const legacy = localStorage.getItem(STOCK_AUDIT_DEVICE_ID_KEY);
    const id = validId(legacy) ? legacy : mintDeviceId();
    localStorage.setItem(WEB_DEVICE_ID_KEY, id);
    // Read back: another tab may have won the race to create it — both tabs then use its id.
    const stored = localStorage.getItem(WEB_DEVICE_ID_KEY);
    return validId(stored) ? stored : null;
  } catch {
    return null;
  }
}

let volatileDeviceId = '';

/**
 * Stock Audit's id for scan rows: the persisted device id, or (storage blocked) one kept in
 * memory for this page load — the scan module's historic behaviour.
 */
export function getOrCreateWebDeviceId(): string {
  const persisted = getPersistedWebDeviceId();
  if (persisted) return persisted;
  if (!volatileDeviceId) volatileDeviceId = mintDeviceId();
  return volatileDeviceId;
}

/** The name an operator gave this browser (the Stock Audit device name), or null. */
export function getWebDeviceName(): string | null {
  try {
    const name = (localStorage.getItem(STOCK_AUDIT_DEVICE_NAME_KEY) || '').trim();
    return name ? name.slice(0, 150) : null;
  } catch {
    return null;
  }
}

/** A short "Chrome 129 / Windows" label — never the raw user agent. */
export function describeWebPlatform(): string {
  let ua = '';
  try {
    ua = navigator.userAgent || '';
  } catch {
    /* ignore */
  }
  const pick = (re: RegExp, name: string): string | null => {
    const m = ua.match(re);
    return m ? `${name} ${m[1]}` : null;
  };
  const browser =
    pick(/Edg(?:e|A|iOS)?\/(\d+)/, 'Edge') ||
    pick(/OPR\/(\d+)/, 'Opera') ||
    pick(/SamsungBrowser\/(\d+)/, 'Samsung Internet') ||
    pick(/(?:Firefox|FxiOS)\/(\d+)/, 'Firefox') ||
    pick(/(?:Chrome|CriOS)\/(\d+)/, 'Chrome') ||
    pick(/Version\/(\d+)[^ ]* (?:Mobile\/\S+ )?Safari/, 'Safari') ||
    'Browser';
  let os = 'Unknown OS';
  if (/Windows/.test(ua)) os = 'Windows';
  else if (/Android/.test(ua)) os = 'Android';
  else if (/iPhone|iPad|iPod/.test(ua)) os = 'iOS';
  else if (/CrOS/.test(ua)) os = 'ChromeOS';
  else if (/Mac OS X|Macintosh/.test(ua)) {
    let touch = false;
    try {
      touch = navigator.maxTouchPoints > 1;
    } catch {
      /* ignore */
    }
    os = touch ? 'iPadOS' : 'macOS';
  } else if (/Linux/.test(ua)) os = 'Linux';
  return `${browser} / ${os}`.slice(0, 100);
}

export interface StoreDeviceSession {
  /** The store token every device-settings request carries. */
  token: string;
  companyId: number;
  branchId: number | null;
}

const claimIsTrue = (v: unknown): boolean => String(v ?? '').trim().toLowerCase() === 'true';

/**
 * The current STORE session of this tab, or null when this tab must not act as a store device
 * (signed out, impersonation popup, developer / reseller token, developer's own session, or a
 * token without a usable company). Synchronous and local: no network.
 */
export function getStoreDeviceSession(): StoreDeviceSession | null {
  try {
    if (OPENED_AS_IMPERSONATION_POPUP) return null;
    if (sessionStorage.getItem('impersonateToken')) return null;
    const token = localStorage.getItem('authToken');
    if (!token) return null;
    const claims = jwtDecode<Record<string, unknown>>(token);
    if (claimIsTrue(claims.isDeveloperLogin) || claimIsTrue(claims.isResellerLogin)) return null;
    if (rolesFromToken(token).includes(DEVELOPER_ROLE)) return null;
    const companyId = Number(claims.companyId);
    if (!Number.isSafeInteger(companyId) || companyId <= 0) return null;
    const branch = Number(claims.branchId);
    return {
      token,
      companyId,
      branchId: Number.isSafeInteger(branch) && branch > 0 ? branch : null
    };
  } catch {
    return null;
  }
}
